import { useEffect } from 'react';
import { GuestType } from '@guest-widgets/shared/src/types/product/guestProduct';
import { Quantity } from '@guest-widgets/shared/src/types/product/miscellaneous';

import {
  GuestTypeRestriction,
  TimeslotRules,
} from '../../contexts/apiContext/timeslot/dtos/response';
import { useProductInSelection } from '../../Product/Guest/productInSelectionContext';
import { TimeslotDayRange } from '../../Product/Steps/Day/Calendar/CalendarRange/useCalendarRange';

function hasGuestTypeRestrictions(
  rules: unknown
): rules is { guestTypeRestrictions: GuestTypeRestriction[] } {
  return Array.isArray((rules as TimeslotRules)?.guestTypeRestrictions);
}

// Initialize default quantities (1 for first guest type, 0 for others)
const initializeDefaultQuantities = (guestTypes: GuestType[]): Quantity => {
  return guestTypes.reduce<Quantity>((acc, guestType, index) => {
    acc[guestType.id] = index === 0 ? 1 : 0;
    return acc;
  }, {});
};

// Distribute total quantity across guest types
const distributeTotalQuantity = (total: number, guestTypes: GuestType[]): Quantity => {
  // Calculate base quantity for each guest type
  const baseQuantity = Math.floor(total / guestTypes.length);
  // Calculate remainder to distribute
  const remainder = total % guestTypes.length;

  return guestTypes.reduce<Quantity>((acc, guestType, index) => {
    // Add 1 to the first 'remainder' number of guest types
    acc[guestType.id] = baseQuantity + (index < remainder ? 1 : 0);
    return acc;
  }, {});
};

// Apply guest type restrictions to set initial quantities
const applyGuestTypeRestrictions = (rules: TimeslotRules, guestTypes: GuestType[]): Quantity => {
  const initialQuantity: Quantity = {};

  // Filter restrictions to only those matching visible guest types
  const visibleRestrictions = rules.guestTypeRestrictions.filter((guesttypeRestriction) =>
    Boolean(guestTypes.find((guestType) => guestType.id === guesttypeRestriction.guestTypeId))
  );

  // Check if at least one guest type has a min quantity
  const hasMinGuests = visibleRestrictions.some((restriction) => restriction.minGuests);

  visibleRestrictions.forEach((restriction, index) => {
    // If at least one guest type has a min quantity, use that quantity or 1 if null
    // otherwise if it's the first guest type min quantity is 1 else 0
    if (hasMinGuests) {
      initialQuantity[restriction.guestTypeId] = restriction.minGuests ?? (index === 0 ? 1 : 0);
    } else {
      initialQuantity[restriction.guestTypeId] = index === 0 ? 1 : 0;
    }
  });

  return initialQuantity;
};

/**
 * Hook to manage guest quantity restrictions
 * @param guestTypes - Array of guest types
 * @param date - Optional date range for availability
 */
export const useQuantityGuestRestrictions = (
  guestTypes: GuestType[] = [],
  date?: TimeslotDayRange
) => {
  const { setQuantity, quantityChanged } = useProductInSelection();

  const setDefaultQuantity = () => {
    let initialQuantity: Quantity = {};
    const availabilityTimes = date?.from?.availabilityTimes ?? date?.to?.availabilityTimes;

    if (Array.isArray(availabilityTimes) && availabilityTimes.length > 0) {
      const rules = availabilityTimes[0].rules as TimeslotRules;

      if (hasGuestTypeRestrictions(rules)) {
        // use in case of minimum quantity is applied to the sum of guesttypes quantities
        const totalGuestQuantityRestriction = rules.guestTypeRestrictions.find(
          (item) => item.guestTypeId === '_total'
        );
        if (!rules.guestTypeRestrictions || rules.guestTypeRestrictions.length === 0) {
          initialQuantity = initializeDefaultQuantities(guestTypes);
        } else if (!!totalGuestQuantityRestriction) {
          const totalMinGuests = totalGuestQuantityRestriction.minGuests || 0;
          initialQuantity = distributeTotalQuantity(totalMinGuests, guestTypes);
        } else {
          initialQuantity = applyGuestTypeRestrictions(rules, guestTypes);
        }
      }

      setQuantity(initialQuantity);
    }
  };

  useEffect(() => {
    // set default quantity only when quantity is not changed from quantity selector or minimum quantity
    if (!quantityChanged) setDefaultQuantity();
  }, [date, setQuantity, guestTypes, quantityChanged]);
};
